<template>
    <v-container fluid>
        <!-- Estudiantes: video predeterminado, instrucciones y slider -->
        <v-row no-gutters v-if="esEstudiante">
            <v-col cols="12">
                <video-predeterminado/>
                <slider-popup contexto="inicio"/>
                <v-card>
                    <v-toolbar color="primary" dark dense>
                        <v-toolbar-title>Instrucciones para Estudiante</v-toolbar-title>
                    </v-toolbar>
                    <v-list>
                        <template v-for="(item, index) in instrucciones">
                            <v-divider v-if="index" :key="'d' + index" inset></v-divider>
                            <v-list-item :key="item">
                                <v-list-item-avatar size="28" color="info">
                                    <span class="white--text">{{ index + 1 }}</span>
                                </v-list-item-avatar>
                                <v-list-item-content>
                                    <v-list-item-title v-html="item"></v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                        </template>
                    </v-list>
                    <div class="pa-4 pt-1" style="max-width: 420px">
                        <tutorial-video variante="claro"/>
                    </div>
                </v-card>
            </v-col>
        </v-row>

        <!-- Cuentas administrativas: tablero -->
        <div v-else class="tablero">
            <div class="tablero__cabecera">
                <div>
                    <h1 class="tablero__titulo">Panel de certificación</h1>
                    <div class="tablero__subtitulo">
                        {{ alcanceTexto }}
                        <span v-if="datos && datos.calculado"> · datos al {{ datos.calculado | fechaESPHora }}</span>
                    </div>
                </div>
                <div class="tablero__filtros">
                    <v-select
                        v-model="filtros.anyo" :items="anyosItems" label="Año"
                        dense outlined hide-details class="filtro filtro--anyo"
                        @change="cargar()"
                    />
                    <v-select
                        v-if="unidades.length > 1"
                        v-model="filtros.unidad_operativa_id" :items="unidadesItems"
                        item-text="unidad_operativa" item-value="unidad_operativa_id"
                        label="Unidad operativa" dense outlined hide-details class="filtro filtro--unidad"
                        @change="cargar()"
                    />
                    <v-btn small outlined color="primary" :loading="actualizando" @click="cargar(true)"
                           title="Vuelve a calcular las cifras con los datos de este momento (tarda medio minuto)">
                        <v-icon small left>refresh</v-icon> Actualizar
                    </v-btn>
                </div>
            </div>

            <v-progress-linear v-if="cargando" indeterminate color="primary" height="3" class="mb-3"/>

            <v-alert v-if="error" dense text type="error" class="mb-3">{{ error }}</v-alert>

            <template v-if="datos">
                <!-- Cifras principales -->
                <div class="cifras">
                    <div class="cifra cifra--principal">
                        <span class="cifra__etiqueta">Certificados emitidos</span>
                        <span class="cifra__valor">{{ n(totales.aprobados) }}</span>
                        <span class="cifra__nota">cursos aprobados</span>
                    </div>
                    <div class="cifra">
                        <span class="cifra__etiqueta">Participantes</span>
                        <span class="cifra__valor">{{ n(totales.participantes) }}</span>
                        <span class="cifra__nota">
                            {{ n(totales.participantes_certificados) }} con al menos un certificado
                        </span>
                    </div>
                    <div class="cifra">
                        <span class="cifra__etiqueta">Tasa de aprobación</span>
                        <span class="cifra__valor">{{ tasa }}%</span>
                        <div class="medidor" role="img" :aria-label="`${tasa}% de aprobación`">
                            <div class="medidor__valor" :style="{width: tasa + '%'}"></div>
                        </div>
                        <span class="cifra__nota">{{ n(totales.reprobados) }} reprobados</span>
                    </div>
                    <div class="cifra">
                        <span class="cifra__etiqueta">Horas certificadas</span>
                        <span class="cifra__valor">{{ compacto(totales.horas) }}</span>
                        <span class="cifra__nota">{{ n(totales.horas) }} horas de formación</span>
                    </div>
                    <div class="cifra">
                        <span class="cifra__etiqueta">Cursos distintos</span>
                        <span class="cifra__valor">{{ n(totales.cursos_distintos) }}</span>
                        <span class="cifra__nota">nombres de curso impartidos</span>
                    </div>
                    <div class="cifra">
                        <span class="cifra__etiqueta">Descargas de certificados</span>
                        <span class="cifra__valor">{{ n(datos.descargas.total_descargas) }}</span>
                        <span class="cifra__nota">
                            <template v-if="datos.descargas.desde">desde el {{ datos.descargas.desde | fechaESP }} · {{ n(datos.descargas.total_verificaciones) }} verificaciones QR</template>
                            <template v-else>se empieza a contar desde hoy</template>
                        </span>
                    </div>
                </div>

                <v-row dense class="mt-1">
                    <!-- Por año -->
                    <v-col cols="12" lg="8">
                        <div class="panel">
                            <div class="panel__cabecera">
                                <div>
                                    <h2>Cursos por año</h2>
                                    <p>Aprobados y reprobados según el año del curso. Haga clic en una barra para filtrar ese año.</p>
                                </div>
                            </div>
                            <apexchart type="bar" height="300" :options="opcionesAnyo" :series="seriesAnyo" @dataPointSelection="elegirAnyo"/>
                        </div>
                    </v-col>

                    <!-- Sexo -->
                    <v-col cols="12" sm="6" lg="4">
                        <div class="panel">
                            <div class="panel__cabecera">
                                <div>
                                    <h2>Participantes por sexo</h2>
                                    <p>Personas distintas con cursos en el alcance elegido.</p>
                                </div>
                            </div>
                            <apexchart type="donut" height="300" :options="opcionesSexo" :series="seriesSexo"/>
                        </div>
                    </v-col>

                    <!-- Por mes -->
                    <v-col cols="12" lg="8">
                        <div class="panel">
                            <div class="panel__cabecera">
                                <div>
                                    <h2>{{ filtros.anyo ? `Cursos finalizados por mes en ${filtros.anyo}` : 'Cursos finalizados por mes' }}</h2>
                                    <p>{{ filtros.anyo ? 'Según la fecha de finalización.' : 'Últimos 24 meses con datos, según la fecha de finalización.' }}</p>
                                </div>
                            </div>
                            <apexchart type="area" height="280" :options="opcionesMes" :series="seriesMes"/>
                        </div>
                    </v-col>

                    <!-- Atención -->
                    <v-col cols="12" sm="6" lg="4">
                        <div class="panel">
                            <div class="panel__cabecera"><div><h2>Requiere atención</h2><p>Pendientes operativos del sistema.</p></div></div>
                            <router-link v-for="p in datos.atencion.pendientes" :key="p.clave" :to="p.ruta" class="pendiente" :class="{'pendiente--ok': !p.cantidad}">
                                <v-icon small :color="p.cantidad ? 'warning' : 'success'">{{ p.cantidad ? p.icono : 'check_circle' }}</v-icon>
                                <span class="pendiente__texto">{{ p.texto }}</span>
                                <span class="pendiente__cantidad">{{ n(p.cantidad) }}</span>
                            </router-link>

                            <h3 class="panel__subtitulo">Últimas importaciones</h3>
                            <div v-for="im in datos.atencion.importaciones" :key="im.importacion_id" class="importacion">
                                <span class="importacion__numero">{{ im.numero }}</span>
                                <span class="importacion__archivo" :title="im.archivo_nombre">{{ im.archivo_nombre }}</span>
                                <span class="importacion__dato">{{ im.procesado ? n(im.registro_cantidad) + ' reg.' : 'Sin procesar' }}</span>
                            </div>
                            <div v-if="!datos.atencion.importaciones.length" class="vacio">Sin importaciones todavía.</div>
                        </div>
                    </v-col>

                    <!-- Top cursos -->
                    <v-col cols="12" lg="6">
                        <div class="panel">
                            <div class="panel__cabecera"><div><h2>Los 10 cursos con más certificados</h2><p>Cursos aprobados por nombre de curso.</p></div></div>
                            <apexchart type="bar" :height="alturaBarras(datos.top_cursos.length)" :options="opcionesTop" :series="seriesTop"/>
                        </div>
                    </v-col>

                    <!-- Departamentos -->
                    <v-col cols="12" lg="6">
                        <div class="panel">
                            <div class="panel__cabecera">
                                <div>
                                    <h2>Participantes por departamento</h2>
                                    <p>Según los dos primeros dígitos de la identidad (departamento donde se inscribió). Los 10 primeros.</p>
                                </div>
                            </div>
                            <apexchart type="bar" :height="alturaBarras(departamentos.length)" :options="opcionesDepartamentos" :series="seriesDepartamentos"/>
                        </div>
                    </v-col>

                    <!-- Unidades -->
                    <v-col cols="12" lg="6" v-if="datos.por_unidad.length > 1">
                        <div class="panel">
                            <div class="panel__cabecera"><div><h2>Cursos por unidad operativa</h2><p>Aprobados y reprobados.</p></div></div>
                            <apexchart type="bar" :height="alturaBarras(datos.por_unidad.length)" :options="opcionesUnidad" :series="seriesUnidad"/>
                        </div>
                    </v-col>

                    <!-- Descargas -->
                    <v-col cols="12" :lg="datos.por_unidad.length > 1 ? 6 : 12">
                        <div class="panel">
                            <div class="panel__cabecera">
                                <div>
                                    <h2>Actividad de los últimos 30 días</h2>
                                    <p>Certificados descargados por los participantes y verificaciones con el código QR.</p>
                                </div>
                            </div>
                            <apexchart v-if="datos.descargas.desde" type="bar" height="260" :options="opcionesDescargas" :series="seriesDescargas"/>
                            <div v-else class="vacio vacio--alto">
                                <v-icon large color="grey lighten-1">insights</v-icon>
                                <span>El sistema empezó a contar las descargas hoy. La gráfica aparecerá con la primera descarga.</span>
                            </div>
                        </div>
                    </v-col>
                </v-row>
            </template>
        </div>
    </v-container>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import Ls from "@/services/ls";
import TutorialVideo from "@/components/Ayuda/TutorialVideo.vue";
import SliderPopup from "@/components/Videos/SliderPopup.vue";
import VideoPredeterminado from "@/components/Videos/VideoPredeterminado.vue";

// Paleta validada para daltonismo (azul y dorado institucionales + verde agua).
const AZUL = "#2f5bd0";
const DORADO = "#c8893a";
const AGUA = "#1baf7a";
const GRIS = "#b9c0d4";
const TINTA = "#1b2340";
const TINTA_SUAVE = "#5b6380";
const REJILLA = "#e9ecf3";

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const formato = new Intl.NumberFormat("es-HN");

const n = (valor) => formato.format(Number(valor) || 0);
const compacto = (valor) => {
    valor = Number(valor) || 0;
    if (valor >= 1e6) return (valor / 1e6).toFixed(1).replace(".0", "") + " M";
    if (valor >= 1e4) return Math.round(valor / 1e3) + " mil";
    return n(valor);
};

// Base común: marcas delgadas, rejilla discreta, texto en tinta (no en el color de la serie).
const base = (extra = {}) => ({
    chart: {toolbar: {show: false}, fontFamily: "inherit", animations: {speed: 350}, zoom: {enabled: false}, ...(extra.chart || {})},
    colors: extra.colors || [AZUL, DORADO],
    dataLabels: {enabled: false},
    grid: {borderColor: REJILLA, strokeDashArray: 0, padding: {left: 8, right: 12}},
    legend: {position: "top", horizontalAlign: "left", fontSize: "12px", labels: {colors: TINTA_SUAVE}, markers: {width: 10, height: 10, radius: 3}, itemMargin: {horizontal: 10}},
    tooltip: {theme: "light", y: {formatter: (v) => n(v)}},
    states: {hover: {filter: {type: "darken", value: 0.9}}, active: {filter: {type: "none"}}},
});

const ejeTexto = {style: {colors: TINTA_SUAVE, fontSize: "11px"}};

export default {
    name: "Inicio",
    components: {apexchart: VueApexCharts, TutorialVideo, SliderPopup, VideoPredeterminado},
    data() {
        return {
            esEstudiante: Number(Ls.get("tipo_persona")) === 3,
            cargando: false,
            actualizando: false,
            error: "",
            datos: null,
            anyos: [],
            unidades: [],
            filtros: {anyo: 0, unidad_operativa_id: 0},
            secuencia: 0,
            instrucciones: [
                "Clic en el menú Mis Cursos.",
                "Elija <b>Historial</b> para ver todos sus cursos, con la nota y la promoción de cada uno.",
                "En los cursos aprobados, clic al botón <b class='info--text'>Descargar certificado</b> para generarlo, descargarlo e imprimirlo.",
                "En <b>Constancias</b> encuentra las constancias emitidas a su nombre.",
            ],
        }
    },
    computed: {
        totales() {
            return this.datos.totales;
        },
        tasa() {
            const total = this.totales.aprobados + this.totales.reprobados;
            return total ? Math.round((this.totales.aprobados * 1000) / total) / 10 : 0;
        },
        anyosItems() {
            // La primera opción devuelve el panel a todos los años (0 = sin filtro).
            return [{text: "Todos los años", value: 0}].concat(this.anyos.map(a => ({text: String(a), value: a})));
        },
        unidadesItems() {
            return [{unidad_operativa: "Todas las unidades operativas", unidad_operativa_id: 0}].concat(this.unidades);
        },
        alcanceTexto() {
            const unidad = this.unidades.find(u => u.unidad_operativa_id === this.filtros.unidad_operativa_id);
            return [this.filtros.anyo ? `Año ${this.filtros.anyo}` : "Todos los años", unidad ? unidad.unidad_operativa : "todas las unidades"].join(" · ");
        },
        departamentos() {
            return this.datos.departamentos.slice(0, 10);
        },

        // ----- Cursos por año (columnas apiladas; el año filtrado se resalta) -----
        seriesAnyo() {
            return [
                {name: "Aprobados", data: this.datos.por_anyo.map(f => Number(f.aprobados))},
                {name: "Reprobados", data: this.datos.por_anyo.map(f => Number(f.reprobados))},
            ];
        },
        opcionesAnyo() {
            const elegido = this.filtros.anyo;
            // Con un año elegido, los demás se atenúan. La librería también llama a esta
            // función para la leyenda, sin punto de datos: ahí va el color pleno.
            const atenuar = (color) => (o) => {
                const fila = o && o.dataPointIndex !== undefined ? this.datos.por_anyo[o.dataPointIndex] : null;
                return !elegido || !fila || Number(fila.anyo) === elegido ? color : GRIS;
            };

            return {
                ...base({chart: {stacked: true, events: {}}}),
                colors: [atenuar(AZUL), atenuar(DORADO)],
                legend: {...base().legend, markers: {...base().legend.markers, fillColors: [AZUL, DORADO]}},
                plotOptions: {bar: {columnWidth: "58%", borderRadius: 3}},
                stroke: {width: 2, colors: ["#fff"]},
                xaxis: {categories: this.datos.por_anyo.map(f => String(f.anyo)), labels: ejeTexto, axisBorder: {show: false}, axisTicks: {show: false}},
                yaxis: {labels: {...ejeTexto, formatter: compacto}},
            };
        },

        // ----- Sexo (dona con el total al centro) -----
        seriesSexo() {
            const s = this.datos.sexo;
            return s.sin_dato ? [s.mujeres, s.hombres, s.sin_dato] : [s.mujeres, s.hombres];
        },
        opcionesSexo() {
            const conSinDato = !!this.datos.sexo.sin_dato;
            return {
                ...base({colors: conSinDato ? [AZUL, DORADO, GRIS] : [AZUL, DORADO]}),
                labels: conSinDato ? ["Mujeres", "Hombres", "Sin dato"] : ["Mujeres", "Hombres"],
                stroke: {width: 2, colors: ["#fff"]},
                legend: {...base().legend, position: "bottom", horizontalAlign: "center", formatter: (nombre, o) => `${nombre}: ${n(o.w.globals.series[o.seriesIndex])}`},
                dataLabels: {enabled: true, formatter: (v) => `${Math.round(v)}%`, style: {fontSize: "13px", fontWeight: 600, colors: ["#fff"]}, dropShadow: {enabled: false}},
                plotOptions: {pie: {donut: {size: "62%", labels: {
                    show: true,
                    name: {color: TINTA_SUAVE, fontSize: "12px"},
                    value: {color: TINTA, fontSize: "22px", fontWeight: 700, formatter: (v) => n(v)},
                    total: {show: true, label: "Participantes", color: TINTA_SUAVE, fontSize: "12px", formatter: (w) => n(w.globals.seriesTotals.reduce((a, b) => a + b, 0))},
                }}}},
            };
        },

        // ----- Por mes (área de dos series) -----
        seriesMes() {
            return [
                {name: "Aprobados", data: this.datos.por_mes.map(f => Number(f.aprobados))},
                {name: "Reprobados", data: this.datos.por_mes.map(f => Number(f.reprobados))},
            ];
        },
        opcionesMes() {
            return {
                ...base(),
                stroke: {curve: "smooth", width: 2},
                fill: {type: "gradient", gradient: {opacityFrom: 0.28, opacityTo: 0.02}},
                markers: {size: 0, hover: {size: 5}},
                xaxis: {
                    categories: this.datos.por_mes.map(f => { const [a, m] = f.mes.split("-"); return `${MESES[Number(m) - 1]} ${a.slice(2)}`; }),
                    tickAmount: 8,
                    labels: {...ejeTexto, rotate: 0, hideOverlappingLabels: true}, axisBorder: {show: false}, axisTicks: {show: false},
                    tooltip: {enabled: false},
                },
                yaxis: {labels: {...ejeTexto, formatter: compacto}},
                tooltip: {...base().tooltip, shared: true, intersect: false},
            };
        },

        // ----- Barras horizontales -----
        seriesTop() {
            return [{name: "Certificados", data: this.datos.top_cursos.map(f => Number(f.aprobados))}];
        },
        opcionesTop() {
            return this.barrasHorizontales(this.datos.top_cursos.map(f => this.titulo(f.curso)), [AZUL], this.datos.top_cursos.map(f => Number(f.aprobados)));
        },
        seriesDepartamentos() {
            return [{name: "Participantes", data: this.departamentos.map(f => f.participantes)}];
        },
        opcionesDepartamentos() {
            return this.barrasHorizontales(this.departamentos.map(f => f.departamento), [AZUL], this.departamentos.map(f => f.participantes));
        },
        seriesUnidad() {
            return [
                {name: "Aprobados", data: this.datos.por_unidad.map(f => Number(f.aprobados))},
                {name: "Reprobados", data: this.datos.por_unidad.map(f => Number(f.reprobados))},
            ];
        },
        opcionesUnidad() {
            const opciones = this.barrasHorizontales(this.datos.por_unidad.map(f => f.unidad), [AZUL, DORADO]);
            return {...opciones, chart: {...opciones.chart, stacked: true}, dataLabels: {enabled: false}, legend: base().legend, stroke: {width: 2, colors: ["#fff"]}};
        },

        // ----- Descargas -----
        seriesDescargas() {
            return [
                {name: "Certificados descargados", data: this.datos.descargas.dias.map(d => d.descargas)},
                {name: "Verificaciones por QR", data: this.datos.descargas.dias.map(d => d.verificaciones)},
            ];
        },
        opcionesDescargas() {
            return {
                ...base({colors: [AZUL, AGUA]}),
                plotOptions: {bar: {columnWidth: "70%", borderRadius: 2}},
                stroke: {width: 2, colors: ["transparent"]},
                xaxis: {
                    categories: this.datos.descargas.dias.map(d => { const [, m, dia] = d.fecha.split("-"); return `${Number(dia)} ${MESES[Number(m) - 1]}`; }),
                    labels: {...ejeTexto, rotate: 0, hideOverlappingLabels: true}, axisBorder: {show: false}, axisTicks: {show: false},
                },
                yaxis: {labels: {...ejeTexto, formatter: (v) => n(Math.round(v))}, forceNiceScale: true, min: 0},
                tooltip: {...base().tooltip, shared: true, intersect: false},
            };
        },
    },
    methods: {
        n,
        compacto,
        titulo(texto) {
            texto = (texto || "").trim();
            // Los nombres vienen en mayúsculas sostenidas: en tipo oración se leen mejor en el eje.
            return texto === texto.toUpperCase() ? texto.charAt(0) + texto.slice(1).toLowerCase() : texto;
        },
        alturaBarras(cantidad) {
            return Math.max(160, cantidad * 34 + 50);
        },
        barrasHorizontales(categorias, colores, valores = []) {
            // El eje llega un 18 % más allá de la barra mayor: ahí cabe su número sin encimarse.
            const maximo = Math.max(0, ...valores);

            return {
                ...base({colors: colores}),
                legend: {show: false},
                plotOptions: {bar: {horizontal: true, barHeight: "62%", borderRadius: 3, dataLabels: {position: "top"}}},
                dataLabels: {enabled: true, textAnchor: "start", offsetX: 30, formatter: (v) => n(v), style: {fontSize: "11px", fontWeight: 600, colors: [TINTA]}},
                xaxis: {categories: categorias, max: maximo ? Math.ceil(maximo * 1.18) : undefined, labels: {show: !maximo, ...ejeTexto, formatter: compacto}, axisBorder: {show: false}, axisTicks: {show: false}},
                yaxis: {labels: {style: {colors: TINTA, fontSize: "12px"}, maxWidth: 260}},
                grid: {...base().grid, xaxis: {lines: {show: !maximo}}, yaxis: {lines: {show: false}}, padding: {left: 8, right: 24}},
            };
        },
        elegirAnyo(evento, contexto, punto) {
            const fila = this.datos.por_anyo[punto.dataPointIndex];
            if (!fila) return;
            const anyo = Number(fila.anyo);
            this.filtros.anyo = this.filtros.anyo === anyo ? 0 : anyo;
            this.cargar();
        },
        cargarFiltros() {
            window.axios.get("/dashboard/filtros").then(response => {
                if (response.data.success) {
                    this.anyos = response.data.data.anyos;
                    this.unidades = response.data.data.unidades;
                }
            }).catch(() => {});
        },
        cargar(refrescar = false) {
            const secuencia = ++this.secuencia;
            this.error = "";
            this.cargando = true;
            this.actualizando = refrescar;

            const params = {};
            if (this.filtros.anyo) params.anyo = this.filtros.anyo;
            if (this.filtros.unidad_operativa_id) params.unidad_operativa_id = this.filtros.unidad_operativa_id;
            if (refrescar) params.refrescar = 1;

            window.axios.get("/dashboard", {params}).then(response => {
                if (secuencia !== this.secuencia) return;

                if (response.data.success) {
                    this.datos = response.data.data;
                    if (refrescar) {
                        this.$toast.success("Cifras actualizadas.");
                        this.cargarFiltros();
                    }
                } else {
                    this.error = response.data.message;
                }
            })
                .catch(error => {
                    if (secuencia !== this.secuencia) return;
                    const cuerpo = error.response && error.response.data;
                    this.error = (cuerpo && cuerpo.message) || "No se pudo cargar el tablero.";
                })
                .finally(() => {
                    if (secuencia !== this.secuencia) return;
                    this.cargando = false;
                    this.actualizando = false;
                });
        },
    },
    created() {
        if (!this.esEstudiante) {
            this.cargarFiltros();
            this.cargar();
        }
    },
}
</script>

<style lang="scss" scoped>
$tinta: #1b2340;
$tinta-suave: #5b6380;
$borde: #e3e7f1;
$azul: #2f5bd0;

.tablero__cabecera {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px 24px;
    margin-bottom: 14px;
}

.tablero__titulo {
    font-size: 1.35rem !important;
    font-weight: 700;
    color: $tinta;
    line-height: 1.2;
}

.tablero__subtitulo {
    font-size: 0.8rem;
    color: $tinta-suave;
}

.tablero__filtros {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
}

.filtro { background: #fff; }
.filtro--anyo { width: 170px; flex: none; }
.filtro--unidad { width: 300px; flex: none; }

.cifras {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
    gap: 10px;
}

.cifra {
    background: #fff;
    border: 1px solid $borde;
    border-radius: 8px;
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.cifra--principal {
    background: #203d89;
    border-color: #203d89;

    .cifra__etiqueta, .cifra__nota { color: rgba(255, 255, 255, 0.78); }
    .cifra__valor { color: #fff; }
}

.cifra__etiqueta {
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: $tinta-suave;
}

.cifra__valor {
    font-size: 1.7rem;
    font-weight: 700;
    line-height: 1.15;
    color: $tinta;
    font-variant-numeric: tabular-nums;
}

.cifra__nota {
    font-size: 0.75rem;
    color: $tinta-suave;
}

.medidor {
    height: 6px;
    border-radius: 3px;
    background: #dfe6f7;
    overflow: hidden;
    margin: 3px 0 2px;
}

.medidor__valor {
    height: 100%;
    border-radius: 3px;
    background: $azul;
}

.panel {
    background: #fff;
    border: 1px solid $borde;
    border-radius: 8px;
    padding: 14px 14px 6px;
    height: 100%;
}

.panel__cabecera {
    margin-bottom: 4px;

    // La hoja global del proyecto agranda todos los h2.
    h2 {
        font-size: 0.95rem !important;
        line-height: 1.3 !important;
        font-weight: 700;
        color: $tinta;
        margin: 0;
    }

    p {
        font-size: 0.76rem;
        color: $tinta-suave;
        margin: 0;
    }
}

.panel__subtitulo {
    font-size: 0.78rem;
    font-weight: 700;
    color: $tinta;
    margin: 14px 0 4px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.pendiente {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 6px;
    border-bottom: 1px solid #eef0f6;
    text-decoration: none;
    color: $tinta;
    font-size: 0.84rem;
    border-radius: 4px;

    &:hover { background: #f5f7fc; }
    &--ok { color: $tinta-suave; }
}

.pendiente__texto { flex: 1; min-width: 0; }

.pendiente__cantidad {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
}

.importacion {
    display: flex;
    gap: 8px;
    align-items: baseline;
    font-size: 0.8rem;
    padding: 5px 6px;
    color: $tinta;
}

.importacion__numero { font-weight: 600; font-variant-numeric: tabular-nums; }
.importacion__archivo { flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: $tinta-suave; }
.importacion__dato { white-space: nowrap; color: $tinta-suave; font-variant-numeric: tabular-nums; }

.vacio {
    font-size: 0.8rem;
    color: $tinta-suave;
    padding: 8px 6px;
}

.vacio--alto {
    min-height: 200px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    text-align: center;
}
</style>
